import React, { useEffect, useState, useRef, useCallback } from "react";
import Header from "../../component/header/header";
import Footer from "../../component/footer/footer";
import { Carousel, Container, Row, Col } from "react-bootstrap";
import "./home.css";
import { useNavigate } from "react-router-dom";
import { FaUsers, FaTools, FaRegHandPointer } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import Form from "../../component/form/form";


const testimonials = [
  {
    name: "Aarav Sharma",
    role: "Startup Founder",
    text: "StartupStiroid helped us hire our first tech team within days. The quality of talent is superb.",
    lottie: "https://assets9.lottiefiles.com/packages/lf20_e3g9n6.json",
  },
  {
    name: "Priya Desai",
    role: "Product Designer",
    text: "I connected with the perfect early-stage startup and scaled design quickly.",
    lottie: "https://assets6.lottiefiles.com/packages/lf20_puciaact.json",
  },
  {
    name: "Michael Lee",
    role: "Investor",
    text: "Great for discovering high-potential founders early. Smooth UX, great signals.",
    lottie: "https://assets4.lottiefiles.com/packages/lf20_tutvdkg0.json",
  },
  {
    name: "Sara Kapoor",
    role: "Founder, FinTech",
    text: "The hiring flow is super smooth and their ATS integration saved us hours.",
    lottie: "https://assets5.lottiefiles.com/packages/lf20_zrqthn6o.json",
  },
  {
    name: "Daniel Carter",
    role: "Marketing Lead",
    text: "Clean UI, a great talent pool, and an extremely helpful community.",
    lottie: "https://assets9.lottiefiles.com/packages/lf20_sSF6EG.json",
  },
];

const chunk = (arr, size) =>
  arr.reduce(
    (acc, _, i) => (i % size === 0 ? [...acc, arr.slice(i, i + size)] : acc),
    []
  );
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};
const foundersData = [
  {
    icon: "🚀",
    title: "Launch Faster",
    text: "Connect directly with founders at top startups - no third party recruiters allowed.",
  },
  {
    icon: "📈",
    title: "Scale Confidently",
    text: "Everything you need to know, all upfront. View salary, stock options, and more before applying.",
  },
  {
    icon: "🤝",
    title: "Connect Smarter",
    text: "Say goodbye to cover letters - your profile is all you need. One click to apply and you're done.",
  },
];
const Home = () => {
  const [animations, setAnimations] = useState({}); // map url -> json
  const [groupSize, setGroupSize] = useState(window.innerWidth < 768 ? 1 : 3);
  const [slides, setSlides] = useState(() => chunk(testimonials, groupSize));
  const [index, setIndex] = useState(0);
  const autoRef = useRef(null);

  // fetch lottie jsons
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const uniqueUrls = Array.from(
          new Set(testimonials.map((t) => t.lottie))
        );
        const res = await Promise.all(
          uniqueUrls.map((u) =>
            fetch(u).then((r) => {
              if (!r.ok) throw new Error("Failed to fetch " + u);
              return r.json();
            })
          )
        );
        if (!mounted) return;
        const map = {};
        uniqueUrls.forEach((u, i) => (map[u] = res[i]));
        setAnimations(map);
      } catch (e) {
        console.warn("Lottie fetch failed:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // recalc group size on resize
  useEffect(() => {
    const onResize = () => {
      const gs = window.innerWidth < 768 ? 1 : 3;
      setGroupSize(gs);
      setSlides(chunk(testimonials, gs));
      setIndex(0);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // setup auto-advance
  useEffect(() => {
    if (autoRef.current) clearInterval(autoRef.current);
    autoRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(autoRef.current);
  }, [slides]);

  // manual navigate
  const goto = useCallback(
    (i) => {
      setIndex(i % slides.length);
      if (autoRef.current) {
        clearInterval(autoRef.current);
        autoRef.current = setInterval(() => {
          setIndex((prev) => (prev + 1) % slides.length);
        }, 4500);
      }
    },
    [slides]
  );

  // card tilt handlers: returns style object for transform
  const tiltRef = useRef({});
  const handleMouseMove = (e, key) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1
    const rotateY = (px - 0.5) * 14; // -7..7 degrees
    const rotateX = (0.5 - py) * 10; // -5..5 degrees
    const translateY = (0.5 - py) * 6;
    tiltRef.current[key] = { rotateX, rotateY, translateY };
    el.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(${translateY}px)`;
    el.style.boxShadow = `0 ${8 + Math.abs(rotateX)}px ${
      22 + Math.abs(rotateY)
    }px rgba(177,0,73,0.12)`;
  };
  const handleMouseLeave = (e, key) => {
    const el = e.currentTarget;
    tiltRef.current[key] = { rotateX: 0, rotateY: 0, translateY: 0 };
    el.style.transform = `perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0px)`;
    el.style.boxShadow = `0 8px 22px rgba(16,8,12,0.06)`;
  };

  React.useEffect(() => {
    const counters = document.querySelectorAll(".count");

    counters.forEach((counter) => {
      const updateCount = () => {
        const target = +counter.getAttribute("data-target");
        const current = +counter.innerText;
        const speed = 80;

        if (current < target) {
          counter.innerText = Math.ceil(current + target / speed);
          setTimeout(updateCount, 40);
        } else {
          counter.innerText = target;
        }
      };

      updateCount();
    });
  }, []);

  const navigate = useNavigate();
  return (
    <div>
      {/* header */}
      <Header />
      {/* hero section */}
      <section className="hero-wrapper">
        <Carousel
          controls={false}
          indicators={false}
          interval={7000} // SLOW SWIPE
          fade={true}
          pause={false}
          className="hero-carousel"
        >
          {/* ========= Slide 1 ========= */}
          <Carousel.Item>
            <video
              className="hero-video"
              src="./images/c-video.mp4"
              autoPlay
              loop
              muted
            />

            <Container>
              <Row className="align-items-center hero-content">
                <Col md={8} sm={12}>
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2 }}
                    className="hero-title"
                  >
                    Empower Your Business Startup Journey
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.3, delay: 0.3 }}
                    className="hero-subtitle"
                  >
                    Transform ideas into a powerful brand with clarity, design &
                    strategy.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.3, delay: 0.6 }}
                  >
                    <button className="hero-btn">Get Started</button>
                    <button className="hero-btn-outline ms-3">
                      Learn More
                    </button>
                  </motion.div>
                </Col>
              </Row>
            </Container>
          </Carousel.Item>

          {/* ========= Slide 2 ========= */}
          <Carousel.Item>
            <img
              className="hero-video"
              src="./images/c-img.jpg"
              alt="carousel-img"
            />

            <Container>
              <Row className="align-items-center hero-content">
                <Col md={8} sm={12}>
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2 }}
                    className="hero-title"
                  >
                    Build Brands That Inspire & Impact
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.3, delay: 0.3 }}
                    className="hero-subtitle"
                  >
                    Smart marketing + beautiful design + powerful storytelling.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.3, delay: 0.6 }}
                  >
                    <button className="hero-btn">Start Now</button>
                    <button className="hero-btn-outline ms-3">
                      Discover More
                    </button>
                  </motion.div>
                </Col>
              </Row>
            </Container>
          </Carousel.Item>
        </Carousel>
      </section>

      {/* secound section */}

      <section className="startup-section">
        <div className="startup-bg"></div>

        <Container>
          {/* Title */}
          <motion.h1
            className="startup-title"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          >
            Solutions • Resources • Company • Careers
          </motion.h1>

          {/* Description */}
          <motion.p
            className="startup-text"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.6, delay: 0.2, ease: "easeOut" }}
          >
            Demo Day is designed for accelerators, investors, and organizers who
            need an efficient method to organize sessions, keep their priorities
            straight, and stay on top of things — the pitches.
          </motion.p>

          <motion.p
            className="startup-text"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.8, delay: 0.3, ease: "easeOut" }}
          >
            It empowers mentors, founders, and investors with powerful tools to
            collaborate and scale business possibilities.
          </motion.p>

          {/* Buttons */}
          <Row className="justify-content-center mt-4">
            <Col xs="auto">
              <motion.button
                className="startup-btn primary-btn"
                onClick={() => navigate("/investor")} // ⭐ Navigate to Investors Page
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.4 }}
              >
                Investors
              </motion.button>
            </Col>

            <Col xs="auto">
              <motion.button
                className="startup-btn outline-btn"
                onClick={() => navigate("/founder")} // ⭐ Navigate to Founders Page
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, delay: 0.6 }}
              >
                Founders
              </motion.button>
            </Col>
          </Row>
        </Container>
      </section>
      {/* third section */}

      {/* ================== COUNTER SECTION ================== */}
      <section className="counter-section">
        <div className="counter-bg"></div>

        <Container>
          <Row className="text-center justify-content-center">
            {/* ===== Counter 1 ===== */}
            <Col md={4} sm={12} className="counter-box">
              <motion.div
                className="icon-glow"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2 }}
              >
                <i className="fas fa-users"></i>
              </motion.div>

              <motion.h1
                className="counter-number"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                <span className="count" data-target="3300">
                  0
                </span>
                +
              </motion.h1>

              <motion.p
                className="counter-label"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1.4 }}
              >
                Investors
              </motion.p>
            </Col>

            {/* ===== Counter 2 ===== */}
            <Col md={4} sm={12} className="counter-box">
              <motion.div
                className="icon-glow"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2 }}
              >
                <i className="fas fa-user-tie"></i>
              </motion.div>

              <motion.h1
                className="counter-number"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                <span className="count" data-target="5000">
                  0
                </span>
                +
              </motion.h1>

              <motion.p
                className="counter-label"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1.4 }}
              >
                Startup Founders
              </motion.p>
            </Col>

            {/* ===== Counter 3 ===== */}
            <Col md={4} sm={12} className="counter-box">
              <motion.div
                className="icon-glow"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2 }}
              >
                <i className="fas fa-rocket"></i>
              </motion.div>

              <motion.h1
                className="counter-number"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                <span className="count" data-target="5">
                  0
                </span>
                +
              </motion.h1>

              <motion.p
                className="counter-label"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1.4 }}
              >
                Years in Business
              </motion.p>
            </Col>
          </Row>
        </Container>
      </section>
      {/* forth section */}

      <section className="founders-wrapper">
        <div className="founders-bg"></div>

        <Container>
          {/* Title */}
          <motion.h1
            className="founders-title"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            For <span>Investor</span>
          </motion.h1>

          <Row className="mt-4">
            {foundersData.map((item, index) => (
              <Col md={4} sm={12} key={index} className="mb-4">
                <motion.div
                  className="founder-card"
                  whileHover={{ scale: 1.05, rotate: -1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <motion.div
                    className="icon-bubble"
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <span className="icon-emoji">{item.icon}</span>
                  </motion.div>

                  <h4 className="founder-card-title">{item.title}</h4>
                  <p className="founder-card-text">{item.text}</p>
                </motion.div>
              </Col>
            ))}
          </Row>

          <motion.button
            className="founders-btn"
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/founders")} // ⭐ OPEN FOUNDERS PAGE
          >
            Learn More
          </motion.button>
        </Container>
      </section>

      {/* fifth section */}

      <section className="founders-wrapper">
        <Container>
          <motion.div className="title-wrapper">
            <motion.h2
              className="founders-title"
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              For <span className="highlight">Founders</span>
            </motion.h2>

            <motion.div
              className="underline"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          </motion.div>

          <Row className="mt-4">
            {/* Point 1 */}
            <Col xs={12} className="mb-4">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                transition={{ duration: 0.6 }}
                className="feature-box"
              >
                <div className="icon-circle">
                  <FaUsers className="feature-icon" />
                </div>
                <p className="feature-text">
                  Tap into a community of 10M+ engaged, startup-ready
                  candidates.
                </p>
              </motion.div>
            </Col>

            {/* Point 2 */}
            <Col xs={12} className="mb-4">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                transition={{ duration: 0.6, delay: 0.1 }}
                className="feature-box"
              >
                <div className="icon-circle">
                  <FaTools className="feature-icon" />
                </div>
                <p className="feature-text">
                  Everything you need to kickstart hiring — job posts, branding,
                  and HR tools within minutes, all for free.
                </p>
              </motion.div>
            </Col>

            {/* Point 3 */}
            <Col xs={12} className="mb-4">
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                transition={{ duration: 0.6, delay: 0.2 }}
                className="feature-box"
              >
                <div className="icon-circle">
                  <FaRegHandPointer className="feature-icon" />
                </div>
                <p className="feature-text">
                  A free applicant tracking system, or easy integration with any
                  HR tool you already use.
                </p>
              </motion.div>
            </Col>
          </Row>

          {/* Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button
              className="learn-btn"
              onClick={() => (window.location.href = "/founder")}
            >
              Learn More
            </button>
          </motion.div>
        </Container>
      </section>

      {/* sixth section */}

      <section className="tp-section" aria-label="Testimonials">
        <div className="tp-container">
          <motion.h3
            className="tp-title"
            initial={{ opacity: 0, y: -18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            What people <span>say</span>
          </motion.h3>

          <div className="tp-carousel-wrap">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                className="tp-slide"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6 }}
              >
                <div className="tp-grid">
                  {slides[index].map((t, i) => (
                    <motion.article
                      key={t.name}
                      className="tp-card glass"
                      onMouseMove={(e) => handleMouseMove(e, `${index}-${i}`)}
                      onMouseLeave={(e) => handleMouseLeave(e, `${index}-${i}`)}
                      whileTap={{ scale: 0.99 }}
                      role="article"
                      aria-label={`Testimonial from ${t.name}`}
                    >
                      <div className="tp-lottie">
                        {animations[t.lottie] ? (
                          <Lottie animationData={animations[t.lottie]} loop />
                        ) : (
                          <div className="lottie-placeholder" />
                        )}
                      </div>

                      <blockquote className="tp-text">“{t.text}”</blockquote>

                      <div className="tp-meta">
                        <div className="tp-avatar">{t.name.charAt(0)}</div>
                        <div className="tp-meta-info">
                          <div className="tp-name">{t.name}</div>
                          <div className="tp-role">{t.role}</div>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* pagination dots */}
          <div
            className="tp-dots"
            role="tablist"
            aria-label="Testimonial pages"
          >
            {slides.map((_, i) => (
              <motion.button
                key={i}
                className={`tp-dot ${i === index ? "active" : ""}`}
                onClick={() => goto(i)}
                aria-label={`Go to slide ${i + 1}`}
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.span
                  className="dot-fill"
                  initial={{ scaleX: i === index ? 1 : 0 }}
                  animate={{ scaleX: i === index ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                />
              </motion.button>
            ))}
          </div>
        </div>
      </section>
      {/* form */}
      <Form/>
            
      {/* footer */}
      <Footer />
    </div>
  );
};

export default Home;
